// Toast queue + mutators for the form-UX-polish surface (P-035).
// Single global queue (Set<listener> shape mirrors Store D-082 +
// persistence-error D-083 + the new lastSavedAt / pendingWrites
// streams), built on the shared `createSubscribable<T>()` factory
// (src/lib/subscribable.ts) hoisted at the 3rd consumer per OC §3.3.
//
// **Discriminated `Toast` union per build-plan default.** Four kinds
// (success / error / info / warning) cover the V1 surfaces:
// - success: "Copied to clipboard" / "Downloaded" / "Opened in tab"
// - error: clipboard failure / download failure / JSON import failure
// - info: reserved (no V1 consumer; kept for forward compat — V2 may
//   surface "Auto-saved 12s ago" or similar)
// - warning: reserved (no V1 consumer; CC API push warnings at V2)
//
// `id` (per-toast unique) enables manual dismiss via `removeToast(id)`
// and the auto-dismiss timer's removal call.
//
// **Auto-dismiss policy (Banks Ask 2 → option a / build-plan default).**
// Non-error kinds auto-dismiss after `AUTO_DISMISS_MS` (4 s); error
// toasts persist until the user clicks the dismiss button. The timer
// lives on the `<ToastContainer>` side per-toast; the store is queue-
// only and stays render-frame-agnostic. The exported constant lets
// tests assert exact-match without redeclaring the value.

import { createSubscribable } from '../../lib/subscribable'
import { newId } from '../../lib/id'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

/**
 * Inline action button rendered inside a toast. Primary V1 consumer is
 * the `<ImageUrlField>` Clear → Undo flow (P-043 / D-129): clearing the
 * image fires an info toast with an Undo action that restores the prior
 * URL value. The container invokes `onClick` and then auto-removes the
 * toast — callers don't need to call `removeToast` from their handler.
 */
export interface ToastAction {
  readonly label: string
  readonly onClick: () => void
}

export interface Toast {
  readonly id: string
  readonly kind: ToastKind
  readonly message: string
  readonly action?: ToastAction
}

export const AUTO_DISMISS_MS = 4_000

/**
 * Hard ceiling on the queue length — defends against runaway error
 * loops (errors persist until manual dismiss; an async retry loop
 * could otherwise balloon the queue indefinitely). Past the ceiling
 * the oldest toast is dropped to make room. 8 fits comfortably on a
 * top-right stack at 1024×768; any genuinely-distinct error stream
 * past 8 is a programming error a single visible toast would already
 * surface.
 */
export const MAX_QUEUE_LENGTH = 8

const queue = createSubscribable<readonly Toast[]>([])

/** Append a new toast and return the generated id (caller-rare use case). */
export function addToast(input: {
  kind: ToastKind
  message: string
  action?: ToastAction
}): string {
  const id = newId()
  queue.update((prev) => {
    const toast: Toast = {
      id,
      kind: input.kind,
      message: input.message,
      ...(input.action !== undefined ? { action: input.action } : {}),
    }
    const next = [...prev, toast]
    return next.length > MAX_QUEUE_LENGTH
      ? next.slice(next.length - MAX_QUEUE_LENGTH)
      : next
  })
  return id
}

/** Remove the toast with the given id. No-op if not present. */
export function removeToast(id: string): void {
  queue.update((prev) => {
    const idx = prev.findIndex((t) => t.id === id)
    if (idx === -1) return prev
    const next = prev.slice()
    next.splice(idx, 1)
    return next
  })
}

/** Read the current toast queue. Imperative variant for tests. */
export function getToasts(): readonly Toast[] {
  return queue.getValue()
}

/** Subscribe to queue changes. Returns an unsubscribe callback. */
export function subscribeToasts(listener: (toasts: readonly Toast[]) => void): () => void {
  return queue.subscribe(listener)
}

/** Test-only: clear the queue between tests. Identity-skipped when already empty. */
export function clearToastsForTest(): void {
  queue.update((prev) => (prev.length === 0 ? prev : []))
}
